'use client'

import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import type { Delivery, Driver, DriverLocation, Store } from '../types';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import logo from '../assets/dest.png';
import { useStores }  from '../hooks/useStores';
import { useDrivers } from '../hooks/useDrivers';
import { fetchActiveDeliveriesFromDb }   from '../services/deliveryService';
import { fetchAllDriverLocationsFromDb } from '../services/driverService';
import { ORDER_STATUS } from '../constants/orderStatuses';

// ── Types ──────────────────────────────────────────────────────────────────────

type DriverEta = {
  mins: number;
  km: string;
  dropOffMins: number;
  dropOffKm: string;
  deliveryPath?: unknown;
  returnPath?: unknown;
};

type FleetItem = Driver & {
  activeDelivery: (Delivery & { driver: Driver & { latest_location: DriverLocation | null } }) | null;
  latest_location: DriverLocation | null;
};

type ExtraFleetItem = FleetItem & { returning_to_store_start_time?: unknown };

// ── Map component ─────────────────────────────────────────────────────────────

type LeafletLayer = { remove: () => void };
type LeafletMap   = { fitBounds: (locs: [number, number][], opts: object) => void };

const DeliveriesMap: FC<{
  deliveries: Delivery[];
  stores: Store[];
  driverEtas: Record<string, DriverEta>;
  allDrivers: FleetItem[];
}> = ({ deliveries, stores, driverEtas, allDrivers }) => {
  const mapRef      = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<LeafletMap | null>(null);
  const layersRef   = useRef<LeafletLayer[]>([]);
  const navigate    = useNavigate();

  useEffect(() => {
    const handlePopupClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('nav-to-driver')) {
        const deliveryId = target.getAttribute('data-delivery-id');
        if (deliveryId) navigate(`/orders/${deliveryId}`, { state: { activeTab: 'deliveries' } });
      }
    };
    document.addEventListener('click', handlePopupClick);
    return () => document.removeEventListener('click', handlePopupClick);
  }, [navigate]);

  const loadLeaflet = () =>
      new Promise<void>((resolve, reject) => {
        const w = window as Window & { L?: unknown };
        if (w.L) { resolve(); return; }
        const script = document.createElement('script');
        script.src   = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload  = () => resolve();
        script.onerror = () => reject(new Error('Leaflet load failed'));
        document.head.appendChild(script);
        const link = document.createElement('link');
        link.rel  = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      });

  useEffect(() => {
    if (stores.length === 0) return;
    let cancelled = false;

    const init = async () => {
      try {
        await loadLeaflet();
        if (cancelled || !mapRef.current) return;
        const w = window as unknown as { L: unknown };
        const L = w.L as {
          map: (el: HTMLElement, opts: object) => LeafletMap;
          tileLayer: (url: string) => { addTo: (m: LeafletMap) => void };
          icon: (opts: object) => unknown;
          marker: (pos: [number, number], opts?: object) => LeafletLayer & { bindPopup: (html: string) => LeafletLayer };
          geoJSON: (data: unknown, opts: object) => LeafletLayer;
        };

        if (!mapInstance.current) {
          mapInstance.current = L.map(mapRef.current, {
            center: [Number(stores[0].latitude), Number(stores[0].longitude)],
            zoom:   12,
          });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(mapInstance.current);
        }

        layersRef.current.forEach(l => l.remove());
        layersRef.current = [];

        const allLocs: [number, number][] = [];
        const storeIcon        = L.icon({ iconUrl: logo, iconSize: [35, 35], iconAnchor: [17, 35] });
        const activeDriverIcon = L.icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/1048/1048313.png', iconSize: [30, 30], iconAnchor: [15, 30] });

        const addToMap = (layer: LeafletLayer) => {
          (layer as LeafletLayer & { addTo: (m: LeafletMap) => void }).addTo(mapInstance.current!);
          layersRef.current.push(layer);
        };

        stores.forEach(s => {
          const marker = L.marker([Number(s.latitude), Number(s.longitude)], { icon: storeIcon });
          marker.bindPopup(`
                        <div style="font-family:sans-serif;padding:4px;min-width:150px;">
                            <div style="font-size:10px;color:#6b7280;text-transform:uppercase;font-weight:bold;margin-bottom:2px;">Store</div>
                            <div style="font-size:14px;font-weight:700;color:#ee2b74;margin-bottom:4px;">${s.name}</div>
                            <div style="font-size:11px;color:#4b5563;border-top:1px solid #f3f4f6;padding-top:4px;">
                                ${(s as Store & { address_line1?: string; suburb?: string }).address_line1 ?? 'No address provided'}, ${(s as Store & { suburb?: string }).suburb ?? ''}
                            </div>
                        </div>
                    `);
          addToMap(marker);
          allLocs.push([Number(s.latitude), Number(s.longitude)]);
        });

        allDrivers.forEach(driver => {
          if (!driver.latest_location) return;
          const dPos: [number, number] = [
            Number(driver.latest_location.latitude),
            Number(driver.latest_location.longitude),
          ];
          const delivery    = driver.activeDelivery;
          const status      = delivery?.status?.toUpperCase();
          const isActive    = !!delivery && [ORDER_STATUS.ON_ROUTE, 'COLLECTED'].includes(status ?? '');
          const isReturning = status === ORDER_STATUS.DELIVERY_COMPLETE || status === ORDER_STATUS.DELIVERY_FAILED;
          const eta         = (isReturning && delivery) ? driverEtas[delivery.id] : null;

          const dMarker = L.marker(dPos, { icon: activeDriverIcon });
          dMarker.bindPopup(`
                        <div style="font-family:sans-serif;min-width:140px;">
                            <div style="font-size:10px;color:#ee2b74;font-weight:bold;text-transform:uppercase;margin-bottom:2px;">Driver</div>
                            <div class="${isActive ? 'nav-to-driver' : ''}" data-delivery-id="${delivery?.order_id ?? ''}"
                                style="font-size:14px;font-weight:700;color:#111827;${isActive ? 'cursor:pointer;text-decoration:underline;' : ''}">
                                ${driver.driver_name}
                            </div>
                            <div style="font-size:11px;color:#6b7280;margin-top:4px;">${driver.car_model} • <b>${driver.number_plate}</b></div>
                            ${eta ? `<div style="margin-top:8px;border-top:1px solid #f3f4f6;padding-top:6px;">
                                <div style="font-size:11px;color:#374151;">To Store: <b>${eta.mins} mins</b></div>
                            </div>` : ''}
                        </div>
                    `);
          addToMap(dMarker);
          allLocs.push(dPos);
        });

        deliveries.forEach(d => {
          const eta    = driverEtas[d.id];
          const status = d.status?.toUpperCase();
          if ((status === ORDER_STATUS.DELIVERY_COMPLETE || status === ORDER_STATUS.DELIVERY_FAILED) && eta?.returnPath) {
            const rPath = L.geoJSON(eta.returnPath, { style: { color: '#36c950', weight: 6, dashArray: '10, 10' } });
            addToMap(rPath);
          }
        });

        if (allLocs.length > 0) {
          mapInstance.current.fitBounds(allLocs, { padding: [50, 50], maxZoom: 15 });
        }
      } catch { /* map errors are non-fatal */ }
    };
    void init();
    return () => { cancelled = true; };
  }, [deliveries, stores, driverEtas, allDrivers]);

  return <div ref={mapRef} className="w-full h-full" style={{ minHeight: '450px', background: '#f5f5f4' }} />;
};

// ── Main page ──────────────────────────────────────────────────────────────────

const ActiveDeliveries: FC = () => {
  const { stores, fetchStores }   = useStores();
  const { drivers, fetchDrivers } = useDrivers();
  const navigate = useNavigate();

  const [fleetItems,        setFleetItems]        = useState<FleetItem[]>([]);
  const [driverEtas,        setDriverEtas]        = useState<Record<string, DriverEta>>({});
  const [selectedStoreId,   setSelectedStoreId]   = useState<string>('all');
  const [activityFilter,    setActivityFilter]    = useState<'all' | 'active' | 'returning' | 'available' | 'offline'>('all');
  const [isStoreFilterOpen, setIsStoreFilterOpen] = useState(false);
  const [searchQuery,       setSearchQuery]       = useState('');

  const calculateAllEtas = useCallback(async (activeDeliveries: Delivery[], currentStores: Store[]) => {
    const newEtas: Record<string, DriverEta> = {};
    await Promise.all(activeDeliveries.map(async delivery => {
      const status = delivery.status?.toUpperCase();
      if (status !== ORDER_STATUS.DELIVERY_COMPLETE && status !== ORDER_STATUS.DELIVERY_FAILED) return;
      const enriched = delivery as Delivery & { driver?: { latest_location?: { latitude?: number; longitude?: number } }; order?: { store_id?: string } };
      const loc      = enriched.driver?.latest_location;
      const storeLoc = currentStores.find(s => String(s.id) === String(enriched.order?.store_id));
      if (!loc?.latitude || !loc?.longitude || !storeLoc?.latitude || !storeLoc?.longitude) return;
      const latNum = Number(loc.latitude);
      const lngNum = Number(loc.longitude);
      if (latNum < -35 || latNum > -22 || lngNum < 16 || lngNum > 33) return;
      try {
        const res   = await fetch(`https://router.project-osrm.org/route/v1/driving/${lngNum},${latNum};${storeLoc.longitude},${storeLoc.latitude}?overview=full&geometries=geojson`);
        const json  = await res.json() as { routes?: { distance: number; duration: number; geometry: { coordinates: number[][] } }[] };
        const route = json.routes?.[0];
        if (route) {
          newEtas[delivery.id] = {
            km:           (route.distance / 1000).toFixed(1),
            mins:         Math.round(route.duration / 60) + 2,
            dropOffMins:  0,
            dropOffKm:    '0',
            deliveryPath: null,
            returnPath:   { type: 'Feature', geometry: { type: 'LineString', coordinates: route.geometry.coordinates } },
          };
        }
      } catch { /* OSRM errors are non-fatal */ }
    }));
    setDriverEtas(prev => ({ ...prev, ...newEtas }));
  }, []);

  const loadData = useCallback(async () => {
    const [delData, locData] = await Promise.all([
      fetchActiveDeliveriesFromDb(),
      fetchAllDriverLocationsFromDb(),
    ]);

    if (drivers.length === 0) return;

    const merged: FleetItem[] = drivers.map(driver => {
      const activeDelivery = delData.find(d =>
          String(d.driver_id) === String(driver.auth_id) &&
          ([ORDER_STATUS.ON_ROUTE, ORDER_STATUS.DRIVER_ASSIGNED, ORDER_STATUS.DELIVERY_COMPLETE, ORDER_STATUS.DELIVERY_FAILED] as string[]).includes(
              (d.status ?? '').toUpperCase()
          )
      );
      const latestLoc = locData.find(l =>
          String(l.driver_id) === String(driver.id) ||
          String(l.driver_id) === String(driver.auth_id)
      ) ?? null;

      return {
        ...driver,
        activeDelivery: activeDelivery
            ? { ...activeDelivery, driver: { ...driver, latest_location: latestLoc } }
            : null,
        latest_location: latestLoc,
      };
    });

    setFleetItems(merged);

    const returning = merged
        .filter(f => {
          const s = f.activeDelivery?.status?.toUpperCase();
          return s === ORDER_STATUS.DELIVERY_COMPLETE || s === ORDER_STATUS.DELIVERY_FAILED;
        })
        .map(f => f.activeDelivery as Delivery);

    if (returning.length > 0 && stores.length > 0) {
      void calculateAllEtas(returning, stores);
    }
  }, [drivers, stores, calculateAllEtas]);

  useEffect(() => {
    void fetchStores();
    void fetchDrivers();
  }, [fetchStores, fetchDrivers]);

  useEffect(() => {
    if (drivers.length === 0) return;
    void loadData();
    const interval = setInterval(() => { void loadData(); }, 30000);
    return () => clearInterval(interval);
  }, [loadData, drivers.length]);

  const filteredFleetItems = fleetItems.filter(item => {
    const extra         = item as ExtraFleetItem;
    const matchesStore  = selectedStoreId === 'all' || String(item.activeDelivery?.order?.store_id) === String(selectedStoreId);
    const matchesSearch = item.driver_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const delivery      = item.activeDelivery;
    const status        = delivery?.status?.toUpperCase();
    const isFailed      = status === ORDER_STATUS.DELIVERY_FAILED;
    const isActive      = !!delivery && status === ORDER_STATUS.ON_ROUTE;
    const isReturning   = (status === ORDER_STATUS.DELIVERY_COMPLETE || isFailed) && !extra.returning_to_store_start_time;
    const isAvailable   = !!extra.returning_to_store_start_time || (!!delivery && status === ORDER_STATUS.DRIVER_ASSIGNED);
    const isOffline     = !delivery && !extra.returning_to_store_start_time;
    const matchesActivity =
        activityFilter === 'all'                        ||
        (activityFilter === 'active'     && isActive)   ||
        (activityFilter === 'returning'  && isReturning) ||
        (activityFilter === 'available'  && isAvailable) ||
        (activityFilter === 'offline'    && isOffline);
    return matchesStore && matchesSearch && matchesActivity;
  });

  const visibleDeliveries = filteredFleetItems
      .filter(item => ([ORDER_STATUS.ON_ROUTE, ORDER_STATUS.DELIVERY_COMPLETE, ORDER_STATUS.DELIVERY_FAILED] as string[]).includes(
          (item.activeDelivery?.status ?? '').toUpperCase()
      ))
      .map(item => item.activeDelivery as Delivery);

  const selectedStore = selectedStoreId === 'all'
      ? null
      : stores.find(s => String(s.id) === String(selectedStoreId)) ?? null;

  const getStatusBadgeStyles = (isActive: boolean, isReturning: boolean, isAvailable: boolean, isFailed: boolean) => {
    if (isFailed)    return 'bg-red-50 text-red-700 ring-red-200';
    if (isActive)    return 'bg-cyan-50 text-cyan-700 ring-cyan-200';
    if (isReturning) return 'bg-amber-50 text-amber-700 ring-amber-200';
    if (isAvailable) return 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]';
    return 'bg-stone-100 text-stone-500 ring-stone-200';
  };

  return (
      <div className="min-h-screen bg-stone-50">
        <div className="mx-auto space-y-6">
          <div className="rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden h-[450px]">
            <DeliveriesMap
                deliveries={visibleDeliveries}
                stores={selectedStore ? [selectedStore] : stores}
                driverEtas={driverEtas}
                allDrivers={filteredFleetItems}
            />
          </div>

          <div className="rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-stone-200 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Driver Activity</h1>
              <div className="flex items-center gap-3">
                <div className="flex bg-stone-100 p-1 rounded-lg border border-stone-200">
                  {(['all', 'active', 'returning', 'available', 'offline'] as const).map(type => (
                      <button key={type} onClick={() => setActivityFilter(type)}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition uppercase ${
                                  activityFilter === type ? 'bg-white text-[#ee2b74] shadow-sm' : 'text-stone-500'
                              }`}>
                        {type}
                      </button>
                  ))}
                </div>

                <div className="relative">
                  <button onClick={() => setIsStoreFilterOpen(!isStoreFilterOpen)}
                          className="flex items-center gap-2 px-4 py-2 border border-stone-300 rounded-lg bg-white text-sm">
                    <span>{selectedStore ? selectedStore.name : 'All Stores'}</span>
                    <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isStoreFilterOpen && (
                      <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-stone-200 bg-white shadow-lg overflow-hidden">
                        <button onClick={() => { setSelectedStoreId('all'); setIsStoreFilterOpen(false); }}
                                className="w-full px-4 py-3 text-left text-sm hover:bg-stone-50 border-b border-stone-100">
                          All stores
                        </button>
                        {stores.map(s => (
                            <button key={s.id} onClick={() => { setSelectedStoreId(String(s.id)); setIsStoreFilterOpen(false); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-stone-50">
                              {s.name}
                            </button>
                        ))}
                      </div>
                  )}
                </div>

                <input type="text" placeholder="Search drivers…" value={searchQuery}
                       onChange={e => setSearchQuery(e.target.value)}
                       className="px-4 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none w-48" />
              </div>
            </div>

            <div className="overflow-x-auto h-72">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-500 border-b border-stone-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold uppercase text-xs">Driver</th>
                  <th className="px-6 py-3 text-left font-semibold uppercase text-xs">Vehicle</th>
                  <th className="px-6 py-3 text-left font-semibold uppercase text-xs">Return ETA</th>
                  <th className="px-6 py-3 text-left font-semibold uppercase text-xs">Status</th>
                  <th className="px-6 py-3 text-left font-semibold uppercase text-xs">Last Sync</th>
                  <th className="px-6 py-3" />
                </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                {filteredFleetItems.map(item => {
                  const extra      = item as ExtraFleetItem;
                  const delivery   = item.activeDelivery;
                  const eta        = delivery ? driverEtas[delivery.id] : null;
                  const status     = delivery?.status?.toUpperCase();
                  const isFailed   = status === ORDER_STATUS.DELIVERY_FAILED && !extra.returning_to_store_start_time;
                  const isActive   = !!delivery && [ORDER_STATUS.ON_ROUTE, 'COLLECTED'].includes(status ?? '');
                  const isReturning = (status === ORDER_STATUS.DELIVERY_COMPLETE || status === ORDER_STATUS.DELIVERY_FAILED) && !extra.returning_to_store_start_time;
                  const isAvailable = !!extra.returning_to_store_start_time || (!!delivery && status === ORDER_STATUS.DRIVER_ASSIGNED);

                  return (
                      <tr key={item.id}
                          className="hover:bg-stone-50 cursor-pointer transition"
                          onClick={() => {
                            if (isActive && delivery) {
                              const orderId = (delivery as Delivery & { order_id?: string }).order_id ?? (delivery as Delivery & { order?: { id?: string } }).order?.id;
                              if (orderId) navigate(`/orders/${orderId}`, { state: { activeTab: 'deliveries' } });
                            }
                          }}>
                        <td className="px-6 py-4">
                          <div className="font-medium text-stone-900">{item.driver_name}</div>
                          <div className="text-[10px] text-stone-400 font-mono uppercase">
                            {isFailed ? (
                                <span className="text-red-500 font-bold">Failed — Returning</span>
                            ) : isReturning ? 'Returning to Store'
                                : isActive ? ((delivery as Delivery & { order?: { reference?: string } }).order?.reference ?? 'Active')
                                    : isAvailable ? 'Ready' : 'Offline'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-stone-600">
                          <div className="text-xs">{item.car_model}</div>
                          <div className="text-[11px] font-semibold text-stone-400 uppercase">{item.number_plate}</div>
                        </td>
                        <td className="px-6 py-4">
                          {isReturning && eta ? (
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#8dc63f]" />
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-stone-400 uppercase font-bold leading-none">To Store</span>
                                  <span className="text-xs font-medium text-stone-700">{eta.mins} mins ({eta.km} km)</span>
                                </div>
                              </div>
                          ) : <span className="text-xs text-stone-300 italic">—</span>}
                        </td>
                        <td className="px-6 py-4">
                                                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold capitalize tracking-wider ring-1 ring-inset ${getStatusBadgeStyles(isActive, isReturning, isAvailable, isFailed)}`}>
                                                    {isFailed ? 'Failed Delivery'
                                                        : isReturning ? 'Returning'
                                                            : isActive ? 'Active Delivery'
                                                                : isAvailable ? 'Available' : 'Offline'}
                                                </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-stone-400">
                          {item.latest_location?.created_at
                              ? new Date(item.latest_location.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : '—'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {isActive && <ChevronRightIcon className="h-5 w-5 text-stone-400" />}
                        </td>
                      </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ActiveDeliveries;